import React, { useEffect, useState } from "react";
import { useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Container, Paper } from "@mui/material";
import { Header, useLanguageTranslator } from "../../../components";
import { PracticeEditor } from "../../../features";

import { useMutation, gql } from "@apollo/client";
import { useNotify } from "../../../components/@common/NotificationsProvider";
import { useApolloQuery } from "../../../hooks";
import { PracticeInputData } from "../../../@lib/types";
import { useAuthSession } from "../../../components/AuthSessionProvider";

const fetchPracticeQueryDocument = gql`
  query practiceQuery($practiceId: ID!) {
    practice(id: $practiceId) {
      title
      description
      documentTitle
      country {
        id
      }
      theme {
        id
      }
      practiceCategory {
        id
      }
      practiceType {
        id
      }
      policies {
        title
        value: id
      }
      people {
        title
        firstname
        lastname
        value: id
      }
      picture {
        source
        originalFilename
      }
      document {
        source
        originalFilename
      }
      link
      date
      status
    }
  }
`;

const updatePracticeMutationDocument = gql`
  mutation UpdatePractice($practiceUpdateInput: PracticeUpdateInput!) {
    practiceUpdate(input: $practiceUpdateInput) {
      practice {
        id
      }
    }
  }
`;

const PracticeEdit: React.FC = () => {
  const [selectedListPolicies, setSelectedListPolicies] = useState<any>([]);
  const [selectedListPeople, setSelectedListPeople] = useState<any>([]);
  const [documentAttachmentDetails, setDocumentAttachmentDetails] =
    useState<any>(null);
  const [imageAttachmentDetails, setImageAttachmentDetails] =
    useState<any>(null);

  const notify = useNotify();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const isAdmin = location?.pathname?.includes(`admin`);

  const { user } = useAuthSession() as any;
  const { translator } = useLanguageTranslator();

  const { data, loading } = useApolloQuery({
    queryDocument: fetchPracticeQueryDocument,
    variables: { practiceId: params.practiceId },
  });

  useEffect(() => {
    if (data?.practice?.policies?.length > 0) {
      const modifiedData = data?.practice?.policies?.map((item: any) => {
        return { label: item?.title, value: item?.value };
      });
      setSelectedListPolicies(modifiedData);
    }

    if (data?.practice?.people?.length > 0) {
      const modifiedData = data?.practice?.people?.map((item: any) => {
        return {
          label: `${item?.firstname} ${item?.lastname} `,
          value: item?.value,
        };
      });
      setSelectedListPeople(modifiedData);
    }
    if (data?.practice?.picture) {
      setImageAttachmentDetails(data?.practice?.picture);
    }
    if (data?.practice?.document) {
      setDocumentAttachmentDetails(data?.practice?.document);
    }
  }, [data]);

  const [updatePractice, { loading: updating }] = useMutation(
    updatePracticeMutationDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const initialValues: PracticeInputData | undefined = useMemo(() => {
    if (!data?.practice) return undefined;

    // Destructure first to get rid of values not accepted as of type `PracticeInput`.
    const {
      id = "",
      country,
      theme,
      practiceCategory,
      practiceType,
      policy,
      ...otherFields
    } = data.practice;

    // Re-structure and return `PracticeInput` type object.
    return {
      ...otherFields,
      countryId: country?.id,
      themeId: theme?.id,
      practiceCategoryId: practiceCategory?.id,
      practiceTypeId: practiceType?.id,
      policyId: policy?.id,
    };
  }, [data]);

  const listOfPoliciesIDs = selectedListPolicies?.map(
    (item: { value: string }) => Number(item?.value)
  );

  const listOfPeopleIDs = selectedListPeople?.map((item: { value: string }) =>
    Number(item?.value)
  );

  /**
   * handleFinish()
   */

  const handleFinish = useCallback(
    async (data: PracticeInputData) => {
      try {
        await updatePractice({
          variables: {
            practiceUpdateInput: {
              id: params.practiceId,
              practiceInput: {
                ...data,
                policies: listOfPoliciesIDs,
                people: listOfPeopleIDs,
                ...(imageAttachmentDetails !== null
                  ? {
                      picture: {
                        source:
                          imageAttachmentDetails?.base64URL ||
                          imageAttachmentDetails?.source,
                        originalFilename:
                          imageAttachmentDetails?.name ||
                          imageAttachmentDetails?.originalFilename,
                      },
                    }
                  : {}),
                ...(documentAttachmentDetails !== null
                  ? {
                      document: {
                        source:
                          documentAttachmentDetails?.base64URL ||
                          documentAttachmentDetails?.source,
                        originalFilename:
                          documentAttachmentDetails?.name ||
                          documentAttachmentDetails?.originalFilename,
                      },
                    }
                  : {}),
              },
            },
          },
        });

        navigate(
          isAdmin
            ? `/admin/practices/${params.practiceId}`
            : `/practices/${params.practiceId}`,
          { replace: true }
        );
      } catch (err) {
        notify.error(err as Error);
      }
    },
    [
      updatePractice,
      navigate,
      params,
      notify,
      imageAttachmentDetails,
      documentAttachmentDetails,
      listOfPoliciesIDs,
      listOfPeopleIDs,
    ]
  );

  /**
   * handleCancel()
   */
  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <Box mb={24}>
      <Header title={translator("Edit practice")} />

      <Container sx={{ my: 3 }}>
        <Paper component={Container} sx={{ py: 3 }}>
          <PracticeEditor
            initialValues={initialValues}
            disabled={loading || updating}
            onFinish={handleFinish}
            onCancel={handleCancel}
            imageAttachmentDetails={imageAttachmentDetails}
            documentAttachmentDetails={documentAttachmentDetails}
            setImageAttachmentDetails={setImageAttachmentDetails}
            setDocumentAttachmentDetails={setDocumentAttachmentDetails}
            setSelectedListPolicies={setSelectedListPolicies}
            selectedListPolicies={selectedListPolicies}
            setSelectedListPeople={setSelectedListPeople}
            selectedListPeople={selectedListPeople}
            isNotPolicyCreateModified={true}
            practiceData={data}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export { PracticeEdit };
